import {InfiniteData, QueryClient} from '@tanstack/react-query'
import {ExportJob, ExportJobListResponse, exportStatusRank} from '@/features/exports/types'
import {EXPORTS_LIST_KEY_PREFIX, ExportsListKeyFilters, exportsDetailKey} from '@/features/exports/query/exportsKeys'

type ExportsInfiniteData = InfiniteData<ExportJobListResponse>

/**
 * Merge an incoming job over the cached one. Status never downgrades (stale-event guard via rank),
 * and the immutable request fields (filters / columns / selection count) are preserved when the
 * incoming payload omits them: socket `status_changed` events and the create response are thin and
 * may not echo these, which would otherwise blank the filters until the next full refetch.
 */
const mergeExportJob = (prev: ExportJob, incoming: ExportJob): ExportJob => {
    const base = exportStatusRank(incoming.status) >= exportStatusRank(prev.status) ? incoming : prev
    return {
        ...base,
        appliedFilters: incoming.appliedFilters ?? prev.appliedFilters,
        appliedColumns: incoming.appliedColumns ?? prev.appliedColumns,
        selectionSummary: incoming.selectionSummary ?? prev.selectionSummary
    }
}

/** Whether a job belongs in a list cached under the given (normalised) filters. */
const jobMatchesFilters = (job: ExportJob, filters: ExportsListKeyFilters): boolean => {
    if (filters.entity && job.entity !== filters.entity) return false
    if (job.status === 'archived' && !filters.includeArchived) return false
    if (filters.status && job.status !== filters.status) return false
    if (filters.format && job.format !== filters.format) return false
    return true
}

/**
 * Apply an incoming job to one cached infinite list:
 * - replace the matching row (ignoring stale/out-of-order transitions via status rank),
 * - drop it if it no longer matches the list's filters (e.g. archived while hidden),
 * - or prepend it when it is new and belongs to this list.
 */
const patchListData = (
    data: ExportsInfiniteData,
    incoming: ExportJob,
    filters: ExportsListKeyFilters
): ExportsInfiniteData => {
    let found = false

    const pages = data.pages.map(page => {
        const items: ExportJob[] = []
        for (const item of page.data) {
            if (item.id !== incoming.id) {
                items.push(item)
                continue
            }
            found = true
            // Stale guard + preserve the immutable request fields the incoming payload may omit.
            const merged = mergeExportJob(item, incoming)
            if (jobMatchesFilters(merged, filters)) items.push(merged)
        }
        return {...page, data: items}
    })

    if (found) return {...data, pages}

    // Not present yet: prepend only if the job belongs to this list.
    if (!jobMatchesFilters(incoming, filters) || pages.length === 0) return data
    const [first, ...rest] = pages
    const newFirst = {
        ...first,
        data: [incoming, ...first.data],
        meta: {...first.meta, total: first.meta.total + 1}
    }
    return {...data, pages: [newFirst, ...rest]}
}

/** Update the single-job cache and every cached export list with an incoming job. */
export const patchExportInCache = (queryClient: QueryClient, job: ExportJob): void => {
    const prevDetail = queryClient.getQueryData<ExportJob>(exportsDetailKey(job.id))
    queryClient.setQueryData(exportsDetailKey(job.id), prevDetail ? mergeExportJob(prevDetail, job) : job)

    const lists = queryClient.getQueriesData<ExportsInfiniteData>({queryKey: EXPORTS_LIST_KEY_PREFIX})
    for (const [key, data] of lists) {
        if (!data) continue
        const filters = (key[2] ?? {}) as ExportsListKeyFilters
        queryClient.setQueryData(key, patchListData(data, job, filters))
    }
}

/** Remove a job from the single-job cache and from every cached export list (after a hard delete). */
export const removeExportFromCache = (queryClient: QueryClient, id: string): void => {
    queryClient.removeQueries({queryKey: exportsDetailKey(id)})

    const lists = queryClient.getQueriesData<ExportsInfiniteData>({queryKey: EXPORTS_LIST_KEY_PREFIX})
    for (const [key, data] of lists) {
        if (!data) continue
        const pages = data.pages.map(page => {
            const items = page.data.filter(item => item.id !== id)
            const removed = page.data.length - items.length
            return removed
                ? {...page, data: items, meta: {...page.meta, total: Math.max(0, page.meta.total - removed)}}
                : page
        })
        queryClient.setQueryData(key, {...data, pages})
    }
}
